import { useState } from 'react'

import { AiOutlinePlus } from 'react-icons/ai'
import {
  CreateResourceLink,
  CreateResourceLinkVariables,
  DeleteResourceLink,
  DeleteResourceLinkVariables,
  GetResourceLinks,
  GetResourceLinksVariables,
  UpdateResourceLink,
  UpdateResourceLinkVariables,
} from 'types/graphql'

import { useMutation, useQuery } from '@cedarjs/web'
import { toast } from '@cedarjs/web/toast'

import { DeleteIcon } from 'src/assets/DeleteIcon'
import HeadingLevel from 'src/components/Custom/Heading/Heading'
import Loader from 'src/components/Custom/Loader/Loader'
import ConfigureResourceLinkModal from 'src/components/Custom/Modal/ConfigureResourceLinkModal'
import CustomModal from 'src/components/Custom/Modal/CustomModal'

import Button from '../../components/Custom/Button/Button'

import {
  CREATE_RESOURCE_LINK,
  DELETE_RESOURCE_LINK,
  GET_RESOURCE_LINKS,
  UPDATE_RESOURCE_LINK,
} from './queries'

export type ResourceLinkType = GetResourceLinks['resourceLinks'][number]

const ResourceLinksSection = () => {
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [editingLink, setEditingLink] = useState<ResourceLinkType>(null)
  const [deletingLink, setDeletingLink] = useState<ResourceLinkType>(null)

  const handleLinkModalClose = () => {
    setShowLinkModal(false)
    setEditingLink(null)
  }

  const {
    data: { resourceLinks = [] } = {},
    loading: loadingResourceLinks,
    refetch: refetchResourceLinks,
  } = useQuery<GetResourceLinks, GetResourceLinksVariables>(GET_RESOURCE_LINKS)

  const [createResourceLink] = useMutation<
    CreateResourceLink,
    CreateResourceLinkVariables
  >(CREATE_RESOURCE_LINK, {
    onCompleted: () => {
      toast.success('Link created')
      handleLinkModalClose()
      refetchResourceLinks()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [updateResourceLink] = useMutation<
    UpdateResourceLink,
    UpdateResourceLinkVariables
  >(UPDATE_RESOURCE_LINK, {
    onCompleted: () => {
      toast.success('Link updated')
      handleLinkModalClose()
      refetchResourceLinks()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [deleteResourceLink] = useMutation<
    DeleteResourceLink,
    DeleteResourceLinkVariables
  >(DELETE_RESOURCE_LINK, {
    onCompleted: () => {
      toast.success('Link deleted')
      setDeletingLink(null)
      setShowConfirmModal(false)
      refetchResourceLinks()
    },
  })

  const handleLinkSave = (input, id: string) => {
    if (id) {
      updateResourceLink({ variables: { id, input } })
    } else {
      createResourceLink({ variables: { input } })
    }
  }

  return (
    <>
      <div className="mt-12 flex items-center justify-between gap-x-2">
        <HeadingLevel level={2} className="align-middle">
          Helpful Resources
        </HeadingLevel>
        <Button
          title="Add link"
          icon={<AiOutlinePlus size="15px" />}
          onClick={() => setShowLinkModal(true)}
          backgroundColor={'bg-indigo-600 hover:bg-indigo-800'}
        />
      </div>
      <div className="mt-8 w-full rounded-lg bg-white p-6 shadow-lg">
        <div className="text-sm text-gray-500">
          Manage helpful links displayed to members
        </div>
        {loadingResourceLinks ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader />
          </div>
        ) : (
          resourceLinks?.length > 0 && (
            <div className="mt-4 grid gap-2">
              {resourceLinks.map((link) => (
                <div
                  key={link.id}
                  className="flex w-full items-center overflow-hidden rounded-lg border border-gray-200 bg-white px-6 py-4"
                >
                  <div className="flex-grow">{link.title}</div>
                  <nav className="rw-table-actions">
                    <button
                      className="rw-button rw-button-small rw-button-blue normal-case"
                      onClick={() => {
                        setEditingLink(link)
                        setShowLinkModal(true)
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="rw-button rw-button-small rw-button-red"
                      title={`Delete link "${link.title}"`}
                      onClick={() => {
                        setDeletingLink(link)
                        setShowConfirmModal(true)
                      }}
                    >
                      <DeleteIcon classStyle="fill-red" />
                    </button>
                  </nav>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {showLinkModal && (
        <ConfigureResourceLinkModal
          header={editingLink?.id ? 'Edit link' : 'Add link'}
          linkData={editingLink}
          showModal={showLinkModal}
          handleModalClose={handleLinkModalClose}
          onSave={handleLinkSave}
        />
      )}

      {showConfirmModal && (
        <CustomModal
          setShowModal={setShowConfirmModal}
          showModal={showConfirmModal}
          modalWidth="max-w-md"
        >
          <HeadingLevel level={3}>Deleting link?</HeadingLevel>
          <p className="pt-2 text-sm text-gray-500">
            {`This action cannot be undone. This will permanently delete the link "${deletingLink.title}".`}
          </p>
          <div className="float-right mt-5 flex gap-2">
            <Button
              title="Cancel"
              customButtonClass={'rounded'}
              onClick={() => setShowConfirmModal(false)}
            />
            <Button
              title="Delete"
              customButtonClass={
                'bg-red-500 rounded font-bold text-white hover:text-white hover:bg-red-700'
              }
              onClick={() => {
                const deletingLinkId = deletingLink?.id
                if (deletingLinkId) {
                  deleteResourceLink({
                    variables: { id: deletingLinkId },
                  })
                }
              }}
            />
          </div>
        </CustomModal>
      )}
    </>
  )
}
export default ResourceLinksSection
